"use client";

import { createContext, useState, type ReactNode } from "react";

import type { getDatoTourInventory } from "@trip/db";

import type { TourCardBlock } from "~/components/structured-text";
import type { GooglePlace } from "~/lib/google";

import type { PlaceCardProps } from "../_lib/gql";

type Inventory = Awaited<ReturnType<typeof getDatoTourInventory>>[0];

export interface PlaceNode {
  node: Omit<PlaceCardProps, "id" | "__typename">;
  googlePlace?: GooglePlace;
}

export interface TourNode {
  node: Omit<TourCardBlock, "id" | "__typename">;
  inventory?: Inventory;
}
interface ActiveSection {
  y: number;
  placeNode: PlaceNode;
}

interface SectionContextType {
  placeNodes: PlaceNode[];
  tourNodes: TourNode[];
  active: PlaceNode | null;
  setActive: (placeNode: PlaceNode | null) => void;
  registerSection: (id: string, y: number, placeNode: PlaceNode) => void;
  unregisterSection: (id: string) => void;
  updateSection: (id: string, y: number) => void;
}

export const Context = createContext<SectionContextType>({
  placeNodes: [],
  tourNodes: [],
  active: null,
  setActive: () => {
    return;
  },
  registerSection: () => {
    return;
  },
  unregisterSection: () => {
    return;
  },
  updateSection: () => {
    return;
  },
});

export function Provider({
  children,
  placeNodes,
  tourNodes,
}: {
  children: ReactNode;
  placeNodes: SectionContextType["placeNodes"];
  tourNodes: SectionContextType["tourNodes"];
}) {
  const [active, setActive] = useState<PlaceNode | null>(null);
  const [_, setRegistry] = useState<Record<string, ActiveSection>>({});

  // Utility: Update active based on the registry
  const updateActiveFromRegistry = (
    newRegistry: Record<string, ActiveSection>,
  ) => {
    const entries = Object.entries(newRegistry);
    if (entries.length === 0) {
      return;
    }
    // Choose the section with the smallest y value (closest to the viewport top)
    const [_, { placeNode }] = entries.reduce((prev, curr) =>
      curr[1].y < prev[1].y ? curr : prev,
    );
    setActive(placeNode);
  };

  const registerSection = (id: string, y: number, placeNode: PlaceNode) => {
    setRegistry((prev) => {
      const newRegistry = { ...prev, [id]: { y, placeNode } };
      updateActiveFromRegistry(newRegistry);
      return newRegistry;
    });
  };

  const unregisterSection = (id: string) => {
    setRegistry((prev) => {
      const newRegistry = { ...prev };
      delete newRegistry[id];
      updateActiveFromRegistry(newRegistry);
      return newRegistry;
    });
  };

  const updateSection = (id: string, y: number) => {
    setRegistry((prev) => {
      if (!prev[id]) return prev; // If not registered, do nothing.
      const newRegistry = { ...prev, [id]: { ...prev[id], y } };
      updateActiveFromRegistry(newRegistry);
      return newRegistry;
    });
  };

  return (
    <Context
      // eslint-disable-next-line @eslint-react/no-unstable-context-value
      value={{
        placeNodes,
        tourNodes,
        active,
        setActive,
        registerSection,
        unregisterSection,
        updateSection,
      }}
    >
      {children}
    </Context>
  );
}
